import { type Dispatch, type SetStateAction } from 'react';

import { CollapsibleSection } from 'cpool-components/general/CollapsibleSection';
import { TEMPLATE_TYPES } from 'cpool-constants';
import type {
    SchemaTemplateResolver_GetMailTemplates,
    SchemaTemplateResolver_GetOfferTemplates,
} from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { TemplateEntryButton } from './TemplateEntryButton';

type Template = SchemaTemplateResolver_GetMailTemplates[number] | SchemaTemplateResolver_GetOfferTemplates[number];

type Props = {
    selectedTemplateId?: string;
    setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
    templates: Template[];
};

export const TemplateList: React.FC<Props> = ({ selectedTemplateId, setSelectedTemplateId, templates }) => {
    const { t } = useTranslation();
    const templateList = templates;

    const mailTemplates = templateList.filter((template) => template.type === TEMPLATE_TYPES.MAIL);
    const offerTemplates = templateList.filter((template) => template.type === TEMPLATE_TYPES.OFFER_MAIL);

    return (
        <aside className="flex h-full w-[246px] shrink-0 flex-col gap-4 overflow-hidden rounded-md bg-surface-light px-2 py-3">
            <p className="flex h-7 shrink-0 items-center px-0.5 text-base font-medium text-midnight">
                {t('MESSAGE.EMAIL.TEMPLATES')}
            </p>
            <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto">
                <CollapsibleSection
                    bgClassNames="overflow-hidden rounded-md border-l-2 border-blue bg-white p-0"
                    defaultOpen
                    headerClassNames="h-8 px-[14px]"
                    keepMounted
                    title={t('MESSAGE.EMAIL.MAIL_TEMPLATES')}
                    titleClassNames="text-sm font-medium text-midnight"
                >
                    {mailTemplates.map((template) => (
                        <TemplateEntryButton
                            key={template.id}
                            template={template}
                            isActive={template.id === selectedTemplateId}
                            onClick={() => setSelectedTemplateId(template.id)}
                        />
                    ))}
                </CollapsibleSection>
                {offerTemplates.length > 0 && (
                    <CollapsibleSection
                        bgClassNames="overflow-hidden rounded-md border-l-2 border-blue bg-white p-0"
                        defaultOpen
                        headerClassNames="h-8 px-[14px]"
                        keepMounted
                        title={t('MESSAGE.EMAIL.OFFER_TEMPLATE')}
                        titleClassNames="text-sm font-medium text-midnight"
                    >
                        {offerTemplates.map((template) => (
                            <TemplateEntryButton
                                key={template.id}
                                hoverClassName="hover:bg-grey-table-hover"
                                template={template}
                                isActive={template.id === selectedTemplateId}
                                onClick={() => setSelectedTemplateId(template.id)}
                            />
                        ))}
                    </CollapsibleSection>
                )}
            </div>
        </aside>
    );
};
